import {
	BusyIndicator,
	CenteredCheckbox,
	DateString,
	Space
} from "client/Components";
import { TimeZone } from "client/DateAndTime/timezones";
import { DestinationWindow } from "client/Api/Apps/common";
import {
	AlertManager,
	useAlerts,
	useUserTimezone,
	useValueEqualMemo
} from "client/Hooks";
import {
	ensureNotNil,
	generateTrackingId,
	isNil
} from "client/Misc/utils";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import { logNever, never } from "client_legacy/ndaw/errors";
import { newGuid, Guid } from "client_legacy/ndaw/guid";
import { navigateTo } from "client_legacy/ndaw/navigator";
import {
	areIdsEqual,
	buildFullUrlWithSingleSignOn,
	encodeGraphIdAccordingToToDoApp,
	InterestedToDoParty,
	ToDoLiveInformationProvider
} from "./provider";
import {
	ToDoListIdentifier,
	ToDoListIdentifierKind
} from "client/Api/Workspace/LiveInformation/ToDos/toDoListLiveInformation";
import {
	ItemsResultStatus,
	ItemsResult,
	ListItemId,
	ToDoListItem
} from "client/Api/Workspace/LiveInformation/ToDos/toDoLiveInformation";
import {
	ReactElement,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState
} from "react";
import { InlineCreationForm } from "./InlineCreationForm";
import { ListItem } from "../ListItem";
import { LiveTileContent } from "../LiveTileContent";
import { TileContext } from "../TileContext";
import { TileRefresh } from "../TileRefresh/TileRefresh";
import { TileTabProps } from "../TileTabSelector";
import styles from "./ToDoLiveTileContent.module.less";
import { useAddAnalyticsAppEvent } from "client/Features/Analytics/useAddAnalyticsAppEvent";
import { LiveTileOverlayState, useLiveTileOverlayState } from "../useLiveTileOverlayState";
import { Coordinate } from "client/Misc/commonTypes";
import { TileStatus } from "../TileStatus";

export type ToDoLiveTileContentProps = {
	destinationWindow: DestinationWindow;
	provider: ToDoLiveInformationProvider;
	onNavigate: (list?: ToDoListIdentifier) => void;
	onOpenTileMenu: (coordinate: Coordinate) => void;
};

export const ToDoLiveTileContent = (props: ToDoLiveTileContentProps): ReactElement => {
	const {
		destinationWindow,
		provider,
		onNavigate,
		onOpenTileMenu
	} = props;

	const [listLoadingState, setListLoadingState] = useState<ListLoadingState>({ kind: "loading" });
	const [activeTabId, setActiveTabId] = useState<Guid | undefined>();
	const [loadingState, setLoadingState] = useState<LoadingState>({ isLoading: true });
	const [pendingCompletion, setPendingCompletion] = useState<Set<ListItemId>>(new Set<ListItemId>());

	const {
		id,
		icon,
		name,
		environmentAppId,
		interactionState,
		dimension,
		appIsLicensed,
		appIsNotAccessible
	} = useContext(TileContext);

	const {
		addEvent: addAnalyticsEvent
	} = useAddAnalyticsAppEvent(environmentAppId);

	const overlayState: LiveTileOverlayState = useLiveTileOverlayState({
		interactionState,
		dimension,
		appIsLicensed,
		appIsNotAccessible
	});

	const timezone: TimeZone = useUserTimezone();
	const alertManager: AlertManager = useAlerts();

	const activeTab = useMemo(
		(): VirtualTab | undefined => {
			if (isNil(activeTabId) || listLoadingState.kind !== "success") {
				return undefined;
			}

			return listLoadingState.tabs
				.find(x => x.uniqueId === activeTabId);
		},
		[activeTabId, listLoadingState]
	);

	const partyInformation = useMemo(
		(): PartyInformation | undefined => {
			if (!activeTab) {
				return undefined;
			}

			return {
				partyId: activeTab.uniqueId,
				resourceId: activeTab.identifier
			};
		},
		[activeTab]
	);

	useEffect(
		(): (() => void) => {
			const loadListAsync = async (signal: AbortSignal): Promise<void> => {
				setListLoadingState({ kind: "loading" });

				try {
					const [defaultListId, lists] = await Promise.all([
						provider.getPreference(id, signal),
						provider.listToDoLists(signal)
					]);

					const tabs: readonly VirtualTab[] = lists.map(
						(x): VirtualTab => ({
							uniqueId: newGuid(),
							identifier: x.identifier,
							name: x.name,
							groupName: x.identifier.kind === ToDoListIdentifierKind.Specific
								? "specific"
								: undefined
						})
					);

					setListLoadingState({
						kind: "success",
						tabs
					});

					const tabToShow: VirtualTab | null = findDefaultTab(tabs, defaultListId);
					if (tabToShow) {
						setActiveTabId(tabToShow.uniqueId);
					}
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}

					console.error(e);
					setListLoadingState({ kind: "error" });
				}
			};

			const cancellation = new AbortController();
			loadListAsync(cancellation.signal);
			return (): void => cancellation.abort();
		},
		[id, provider]
	);

	const stablePartyInformation = useValueEqualMemo(
		(): PartyInformation | undefined => partyInformation,
		[partyInformation]
	);

	useEffect(
		(): (() => void) | undefined => {
			if (!stablePartyInformation) {
				return;
			}

			setLoadingState({ isLoading: true });

			const party: InterestedToDoParty = {
				partyId: stablePartyInformation.partyId,
				resourceId: stablePartyInformation.resourceId,
				informAboutGlobalRetrievalFailure: () => {
					setLoadingState({
						isLoading: false,
						error: "global"
					});
				},
				informAboutConsecutiveErrors: () => {
					setLoadingState({
						isLoading: false,
						error: "consecutive"
					});
				},
				updateItems: (result: ItemsResult) => {
					setLoadingState({
						isLoading: false,
						result
					});
				},
				pendingTaskCompletion: (id: ListItemId) => {
					setPendingCompletion(
						(x) => {
							const result = new Set<ListItemId>(x);
							result.add(id);
							return result;
						}
					);
				},
				taskCompletionFailed: (id: ListItemId) => {
					setPendingCompletion(
						(x) => {
							const result = new Set<ListItemId>(x);
							result.delete(id);
							return result;
						}
					);
				},
				taskCompleted: (id: ListItemId) => {
					setPendingCompletion(
						(x) => {
							const result = new Set<ListItemId>(x);
							result.delete(id);
							return result;
						}
					);

					setLoadingState(
						(x): LoadingState => {
							if (x.isLoading || x.error !== undefined) {
								return x;
							}

							const items: ToDoListItem[] = x.result
								.items
								.filter(
									(x) => !areIdsEqual(x.id, id)
								);

							return {
								...x,
								result: {
									...x.result,
									items
								}
							};
						}
					);
				}
			};

			provider.register(
				party,
				{ items: true }
			);

			return (): void => provider.unregister(party);
		},
		[
			stablePartyInformation,
			provider
		]
	);

	const taskInformationClickHandler = useCallback(
		(taskId: ListItemId): void => {
			const url: string = buildFullUrlWithSingleSignOn(`/tasks/id/${encodeGraphIdAccordingToToDoApp(taskId.id)}/details`);
			navigateTo(url, destinationWindow === DestinationWindow.NewWindow);
		},
		[destinationWindow]
	);

	const completeTaskHandler = useCallback(
		(taskId: ListItemId): void => {
			const { resourceId } = ensureNotNil(stablePartyInformation);
			provider.completeTask(resourceId, taskId);
		},
		[provider, stablePartyInformation]
	);

	const createTaskHandler = useCallback(
		async (name: string): Promise<TryResult<void>> => {
			if (!activeTab) {
				return { wasSuccessful: false };
			}

			addAnalyticsEvent();

			try {
				await provider.createTask(activeTab.identifier, name);
				alertManager.showSuccess(localize("_SuccessfullyCreatedTheToDo"), 1);
				return { wasSuccessful: true, result: undefined };
			} catch (e) {
				console.error(e);
				alertManager.showError(localize("_FailedToCreateTheToDo"));
				return { wasSuccessful: false };
			}
		},
		[
			activeTab,
			addAnalyticsEvent,
			alertManager,
			provider
		]
	);

	const navigateHandler = useCallback(
		(): void => {
			addAnalyticsEvent();
			onNavigate(activeTab?.identifier);
		},
		[
			activeTab?.identifier,
			addAnalyticsEvent,
			onNavigate
		]
	);

	const changeTabHandler = useCallback(
		(tabId: Guid): void => {
			setActiveTabId(tabId);

			const tabs: readonly VirtualTab[] = listLoadingState.kind === "success"
				? listLoadingState.tabs
				: [];

			const tab: VirtualTab = ensureNotNil(
				tabs.find((x) => x.uniqueId === tabId)
			);

			provider.setPreference(id, tab.identifier)
				.catch((e) => console.error(`Failed to set the todo tile preference (${id})`, e));
		},
		[
			id,
			listLoadingState,
			provider
		]
	);

	const itemClickHandler = useCallback(
		(id: ListItemId): void => {
			addAnalyticsEvent();
			taskInformationClickHandler(id);
		},
		[
			addAnalyticsEvent,
			taskInformationClickHandler
		]
	);

	const renderContent = (): ReactElement | null => {
		switch (listLoadingState.kind) {
			case "loading":
				return null;
			case "error":
				return (
					<LiveTileContent.Message text={localize("_ErrorDuringToDosRetrieval")} />
				);
			case "success":
				// continue down below
				break;
			default:
				never(listLoadingState);
		}

		if (loadingState.isLoading) {
			return null;
		}

		if (loadingState.error !== undefined) {
			switch (loadingState.error) {
				case "consecutive":
					return (
						<TileRefresh onRefresh={() => provider.forceReload()} />
					);
				case "global":
					return (
						<LiveTileContent.Message text={localize("_ErrorDuringToDosRetrieval")} />
					);
			default:
				never(loadingState);
			}
		}

		const { result } = loadingState;
		switch (result.status) {
			case ItemsResultStatus.GeneralFailure:
				return (
					<LiveTileContent.Message text={localize("_ErrorDuringToDosRetrieval")} />
				);
			case ItemsResultStatus.NoAccess:
				return (
					<LiveTileContent.Message text={localize("_YouDontHaveThePermissionsRequiredToViewThisList")} />
				);
			case ItemsResultStatus.Success:
				// continue down below
				break;
			default:
				never(result.status);
		}

		const { items } = result;

		if (items.length === 0) {
			return (
				<LiveTileContent.Message text={localize("_YouDontHaveAnyToDosInThisList")} />
			);
		}

		return (
			<LiveTileContent.List>
				{items.map((x): ReactElement => (
					<ListItem
						key={`${x.id.kind}_${x.id.id}`}
						trackingId={generateTrackingId("todo", `|${x.id.kind}_${x.id.id}`)}
						isHoverable
						hasSeparateClickableAreas
						title={x.name}
					>
						<div className={styles.checkboxContainer}>
							<CenteredCheckbox
								{...pendingCompletion.has(x.id)
									? {
										value: true,
										isDisabled: true
									}
									: {
										value: false,
										onChange: () => completeTaskHandler(x.id)
									}
								}
							/>
						</div>

						<ListItem.ClickableArea
							position="last"
							shouldGrow
							onClick={() => itemClickHandler(x.id)}
							isDisabled={pendingCompletion.has(x.id)}
						>
							<ListItem.Content>
								<ListItem.Title
									isStrikedThrough={pendingCompletion.has(x.id)}
								>
									{x.name}
								</ListItem.Title>
								{x.dueDate && (
									<ListItem.Info>
										{localize("_Due")}
										<Space />
										<DateString
											value={x.dueDate}
											format="RelativeDay"
											timezone={timezone}
										/>
									</ListItem.Info>
								)}
							</ListItem.Content>
						</ListItem.ClickableArea>
					</ListItem>
				))}
			</LiveTileContent.List>
		);
	};

	const showCreateOption: boolean = activeTab?.identifier.kind === ToDoListIdentifierKind.Default
		|| activeTab?.identifier.kind === ToDoListIdentifierKind.Specific;

	return (
		<LiveTileContent
			name={name}
			icon={icon}
			overlay={overlayState}
			onOpenTileMenu={onOpenTileMenu}
			overlayStatusIcon={<TileStatus />}
		>
			<LiveTileContent.Header icon={icon}>
				<LiveTileContent.TabSelector
					value={activeTabId}
					tabs={listLoadingState.kind === "success"
						? listLoadingState.tabs
						: []
					}
					onChange={changeTabHandler}
					tileId={id}
					tileName={name}
				/>
			</LiveTileContent.Header>
			<LiveTileContent.Content>
				<LiveTileContent.ContentStack>
					{showCreateOption && (
						<InlineCreationForm
							onCreate={createTaskHandler}
						/>
					)}
					<LiveTileContent.BusyIndicatorContainer>
						{(listLoadingState.kind === "loading" || (listLoadingState.kind !== "error" && loadingState.isLoading)) && (
							<BusyIndicator />
						)}
						<LiveTileContent.Scrollable>
							{renderContent()}
						</LiveTileContent.Scrollable>
					</LiveTileContent.BusyIndicatorContainer>
				</LiveTileContent.ContentStack>
			</LiveTileContent.Content>

			<LiveTileContent.Footer
				name={name}
				onClick={navigateHandler}
			/>
		</LiveTileContent>
	);
};
ToDoLiveTileContent.displayName = "ToDoLiveTileContent";

type ListLoadingState =
	| { kind: "loading"; }
	| { kind: "error"; }
	| { kind: "success"; tabs: readonly VirtualTab[]; };

type VirtualTab = TileTabProps & {
	identifier: ToDoListIdentifier;
};

type PartyInformation = {
	partyId: Guid;
	resourceId: ToDoListIdentifier;
};

function findDefaultTab(
	tabs: readonly VirtualTab[],
	identifier: ToDoListIdentifier | undefined
): VirtualTab | null {
	if (identifier) {
		for (const tab of tabs) {
			const tabIdentifier: ToDoListIdentifier = tab.identifier;

			switch (identifier.kind) {
				case ToDoListIdentifierKind.Default:
					if (tabIdentifier.kind === ToDoListIdentifierKind.Default) {
						return tab;
					}
					break;
				case ToDoListIdentifierKind.Specific:
					if (
						tabIdentifier.kind === ToDoListIdentifierKind.Specific
						&& tabIdentifier.id === identifier.id
					) {
						return tab;
					}
					break;
				case ToDoListIdentifierKind.Planner:
					if (tabIdentifier.kind === ToDoListIdentifierKind.Planner) {
						return tab;
					}
					break;
				case ToDoListIdentifierKind.WellKnown:
					if (tabIdentifier.kind === ToDoListIdentifierKind.WellKnown
						&& tabIdentifier.wellKnownListKind === identifier.wellKnownListKind
						&& tabIdentifier.id === identifier.id
					) {
						return tab;
					}
					break;
				default:
					logNever(identifier);
					break;
			}
		}
	}

	return tabs
		.filter(
			(x) => x.identifier.kind === ToDoListIdentifierKind.Default
		)[0] ?? null;
}

type LoadingState = {
	isLoading: true;
} | {
	isLoading: false;
} & (
		{
			error: "consecutive" | "global";
		}
		|
		{
			error?: undefined;
			result: ItemsResult;
		}
	);